import styled from 'styled-components';
import { ReactNode, useEffect, useRef } from 'react';
import { useService } from '@visiba/instantiation';
import { usePresentation } from '@visiba/presentation';
import { ChatInputArea } from './_chat_input_area.component';
import { ChatMessagesContainer } from './_chat_messages_container.component';
import { ChatController } from './chat.controller';
import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { ChatCommunicatorProtocol } from './chat_communicator_protocol';
import { quirk_iOSKeyboardLockBodyScroll } from '@module/quirk_ios_keyboard_lock_body_scroll';
import { ObfuscateNameContext } from './_obfuscate_name.context';

interface Props {
	communicator: ChatCommunicatorProtocol;
	/**
	 * @deprecated
	 */
	connectedId: string;
	isReadyToJoin?: boolean;
	header?: ReactNode | ReactNode[];
	forceHideActions?: boolean;
	hasReadUntilData?: VisibaDate;
	showTopBanner?: ReactNode;
	showBottomBanner?: ReactNode;
	issuesTrackedById?: string;
	onMarkAsCompleted?: () => void;
	onReceivedHistory?: () => void;
	onSendMessage?: () => void;
	obfuscateName?: boolean;
}

const elements = {
	container: styled.div`
		display: flex;
		flex-direction: column;
		height: 100%;
		flex: 1;
	`,
	header: styled.div`
		width: 100%;
	`,
};

export function Chat({
	communicator,
	connectedId,
	header,
	forceHideActions,
	isReadyToJoin,
	hasReadUntilData,
	showTopBanner,
	showBottomBanner,
	issuesTrackedById,
	onMarkAsCompleted,
	onReceivedHistory,
	onSendMessage,
	obfuscateName = false,
}: Props): JSX.Element {
	const controller = useService(ChatController);
	const presentation = usePresentation(controller.presentation);
	const analyticCtx = useAnalyticContext();
	const containerRef = useRef<HTMLDivElement>(null);

	function handleSend(message: string | null, fileTickets: string[]): void {
		controller.sendMessage(message, fileTickets);
		analyticCtx.trackEvent('User clicked on send message button');

		if (onSendMessage != null) {
			onSendMessage();
		}
	}

	useEffect(
		function whenUserIsReadyToJoinRoom() {
			if (isReadyToJoin) {
				controller.join(
					connectedId,
					{
						issuesTrackedById,
						hasReadUntilTimestamp: hasReadUntilData ?? null,
					},
					communicator,
				);
			}
		},
		[controller, connectedId, hasReadUntilData, isReadyToJoin, issuesTrackedById, communicator],
	);

	useEffect(
		function whenComponentUnmounts() {
			return () => {
				controller.terminate();
			};
		},
		[controller],
	);

	useEffect(
		function whenConnectedIdChanges() {
			return () => {
				controller.removeCurrentBridge();
			};
		},
		[controller, connectedId],
	);

	return (
		<ObfuscateNameContext.Provider value={obfuscateName}>
			<elements.container
				ref={containerRef}
				onBlur={() => {
					quirk_iOSKeyboardLockBodyScroll();
				}}
			>
				<elements.header>{header}</elements.header>
				<ChatMessagesContainer
					bridge={presentation.communicator}
					showLoader={!presentation.isReadyForCommunication}
					markAsCompleted={forceHideActions ?? false}
					topBanner={showTopBanner}
					bottomBanner={showBottomBanner}
					onMarkAsCompleted={onMarkAsCompleted}
					onReceivedHistory={onReceivedHistory}
				/>
				{!forceHideActions ? (
					<ChatInputArea
						connectedId={connectedId}
						fileUploadDispositionUri={communicator.fileUploadDispositionUri}
						onSend={handleSend}
						isReadyForCommunication={presentation.isReadyForCommunication}
					/>
				) : null}
			</elements.container>
		</ObfuscateNameContext.Provider>
	);
}
